import { z } from 'zod';

export const WEEK_DAYS = [
  { key: 'monday', label: 'Lun' },
  { key: 'tuesday', label: 'Mar' },
  { key: 'wednesday', label: 'Mié' },
  { key: 'thursday', label: 'Jue' },
  { key: 'friday', label: 'Vie' },
  { key: 'saturday', label: 'Sáb' },
  { key: 'sunday', label: 'Dom' },
] as const;

export type WeekDayKey = (typeof WEEK_DAYS)[number]['key'];

export const WEEK_DAY_KEYS: WeekDayKey[] = WEEK_DAYS.map((day) => day.key);

export type WeekDaysFlags = Record<WeekDayKey, boolean>;

export interface PickupCycle extends WeekDaysFlags {
  id: string;
  commerceId: string;
  branchId: string | null;
  cycleTime: string;
  tag: string | null;
  commerce?: {
    id: string;
    name: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface PickupCyclesFilter {
  commerceId?: string;
  branchId?: string;
  tag?: string;
  // Hora de la vuelta en formato "HH:mm".
  cycleTime?: string;
  // Filtro "multidía": el ciclo debe estar activo en todos los días marcados.
  days?: WeekDayKey[];
}

export interface CreatePickupCyclePayload extends WeekDaysFlags {
  commerceId: string;
  branchId: string | null;
  cycleTime: string;
  tag: string | null;
}

/**
 * Alta del mismo retiro para varias sucursales de un comercio. `branchIds`
 * vacío significa "casa central" (branchId nulo), igual que el alta de a una.
 */
export interface BulkCreatePickupCyclePayload extends WeekDaysFlags {
  commerceId: string;
  branchIds: string[];
  cycleTime: string;
  tag: string | null;
}

export interface BulkCreatePickupCycleResponse {
  created: PickupCycle[];
  skipped: { branchId: string | null; reason: string }[];
}

export interface PickupCommerceOption {
  id: string;
  name: string;
}

export interface PickupBranchAddress {
  id: string;
  streetName: string;
  doorNumber: string;
  apartmentNumber?: string | null;
  corner?: string | null;
  zipCode?: string | null;
  neighborhoodId?: string | null;
  lat?: string | number | null;
  long?: string | number | null;
}

export interface PickupBranch {
  id: string;
  name: string;
  businessName?: string;
  commerceId: string;
  addressId?: string | null;
  address?: PickupBranchAddress | null;
}

export interface UpdateBranchAddressPayload {
  name: string;
  businessName: string;
  commerceId: string;
  streetName: string;
  doorNumber: string;
  apartmentNumber?: string;
  corner?: string;
  zipCode?: string;
  neighborhoodId?: string;
}

// Valor centinela para representar la opción "Casa central (por defecto)" en el
// select de sucursal. Al enviar al back se traduce a branchId = null.
export const DEFAULT_BRANCH_VALUE = '__casa_central__';

export const isDefaultBranchValue = (value: string | null | undefined) =>
  !value || value === DEFAULT_BRANCH_VALUE;

export interface PickupCycleFormValues extends WeekDaysFlags {
  commerceId: string;
  /**
   * Sucursal única. Sólo se usa al editar, porque editar es crear el retiro
   * nuevo y borrar el viejo: si un alta de edición se abriera a varias
   * sucursales, el borrado dejaría a la original sin retiro.
   */
  branchId: string;
  /** Sucursales del alta. El centinela de casa central sólo vale solo. */
  branchIds: string[];
  cycleTime: Date | null;
  tag: string;
}

export const pickupCycleSchema = z
  .object({
    commerceId: z.string().min(1, 'Elegí un comercio'),
    branchId: z.string(),
    branchIds: z.array(z.string()),
    cycleTime: z.date().nullable(),
    tag: z
      .string()
      .max(255, 'La etiqueta no puede superar los 255 caracteres'),
    monday: z.boolean(),
    tuesday: z.boolean(),
    wednesday: z.boolean(),
    thursday: z.boolean(),
    friday: z.boolean(),
    saturday: z.boolean(),
    sunday: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.cycleTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cycleTime'],
        message: 'Ingresá la hora de la vuelta',
      });
    }

    if (data.branchIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['branchIds'],
        message: 'Elegí al menos una sucursal',
      });
    }

    const hasAtLeastOneDay = WEEK_DAY_KEYS.some((key) => data[key]);
    if (!hasAtLeastOneDay) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['days'],
        message: 'Marcá al menos un día',
      });
    }
  });

export const defaultPickupCycleFormValues: PickupCycleFormValues = {
  commerceId: '',
  branchId: DEFAULT_BRANCH_VALUE,
  branchIds: [DEFAULT_BRANCH_VALUE],
  cycleTime: null,
  tag: '',
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
};
